import { State, StateContext, Action, Store } from '@ngxs/store';
import { AmberError, AmberResponse } from 'src/app/snatch/models/amber-response.model';
import { Product } from '../models/product.model';
import {
  ToggleTab,
  GetProductDetails,
  GetProductDetailsSuccess,
  GetProductDetailsFailure,
  OnChangeVariantForm,
  RestoreState,
  GetProductVariantDeliverable,
  GetProductVariantDeliverableSuccess,
  GetProductVariantDeliverableFailure,
  OnSelectedVariantChange,
  RequestEvaluationCopy,
  RequestEvaluationCopySuccess,
  RequestEvaluationCopyError,
  ClearProductDetailsState,
  GetAccess,
  CheckSegments,
  SelectedVariantQuantityChange,
  SelectProductVariant,
  GetRelatedProducts,
  GetRelatedProductsSuccess,
  GetRelatedProductsError,
  ComputePriceForOrderTemplate,
  FetchOrderTemplatesMinError,
  FetchOrderTemplatesMinSuccess,
  FetchOrderTemplatesMin,
  CreateOrderTemplate,
  AddOrderTemplateItem,
  UpdateProductDetails,
  GetProductRelatedBundles,
  GetIncludedProducts,
  SetIncludedProductsPagination,
  OnChangeBundleVariantForm, SetProductRelatedBundlesPagination,
} from "../actions/product-details.actions";
import { UrlStorageService } from '../services/url-storage.service';
import { ProductDetailsService } from '../services/product-details.service';
import {map, catchError, tap, debounceTime, filter} from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ProductVariantShort } from 'src/app/common/common-product/models/product-short.model';
import {
  ListProductVariantDeliverableModel,
  ProductVariantDeliverableModel
} from "../models/product-variant-deliverable.model";
import { EnvironmentService } from 'src/app/snatch/services';
import { Contract } from 'src/app/snatch/models/contract.model';
import { differenceInDays } from 'date-fns';
import { ProductVariantPricing } from "../../common/common-product/models/product-variant-pricing.model";
import { AuthTokenState } from 'src/app/auth/state/auth-token.state';
import { UtilsService } from "../../snatch/services/utils.service";
import { GraphQLError } from "../../snatch/models/graphql-error.model";
import { OrderTemplatesService } from "../../order-templates/services/order-templates.service";
import {
  ClearOrderTemplateState
} from "../../order-templates/actions/order-templates.actions";
import { Configuration } from "../../snatch/models/configuration.model";
import {BundleModel, CommonPricingQueryModel} from "../models/bundle.model";
import { IPageable } from 'src/app/inbox/state/inbox.state';

export type ProductDetailsTab = 'about' | 'content' | 'course-information' | 'how-to-access' | 'delivery-and-specifications' | 'reviews' | 'q-and-a'
export interface IVariantForm {
  sku: string;
  variantSku: string;
  language: string;
  deliveryFormat: string;
  certificateType: string;
}

export interface ProductDetailsStateModel {
  details: Product;
  relatedProducts: Array<any>;

  relatedBundles: BundleModel[];
  relatedBundlesTotalCount: number;
  relatedBundlesPagination: IPageable;

  includedProducts: Product[];
  includedProductsTotalCount: number;
  includedProductsPagination: IPageable;

  getProductDetailsPending: boolean;
  getProductDetailsSuccess: boolean;
  getProductDetailsError: AmberError;

  createOrderTemplatePending: boolean;
  createOrderTemplateError: AmberError;
  orderTemplatesList: Array<any>;

  addOrderTemplateItemPending: boolean;
  addOrderTemplateItemSuccess: boolean;
  addOrderTemplateItemError: AmberError,

  fetchOrderTemplatesMinPending: boolean;
  fetchOrderTemplatesMinSuccess: boolean;
  fetchOrderTemplatesMinError: AmberError;

  licenseForm: { model: CommonPricingQueryModel },
  variantForm: { model: IVariantForm };
  selectedVariant: ProductVariantShort;
  selectedVariantQuantityChangePending: boolean,
  selectedVariantQuantityChangeSuccess: boolean,
  selectedVariantQuantityChangeError: AmberError,

  languages: Configuration[];
  deliveryFormats: Configuration[];
  certificateTypes: Configuration[];
  productVariantDeliverables: Map<string, Array<ProductVariantDeliverableModel>>;
  productVariantDeliverablesPending: boolean;
  productVariantDeliverablesSuccess: boolean;

  requestEvaluationCopySuccess: boolean;
  requestEvaluationCopyPending: boolean;
  requestEvaluationCopyError: any;

  segmentsMapper: Map<string, Contract>;
  expiringContract: Contract;

  getRelatedProductsPending: boolean;
  getRelatedProductsSuccess: boolean;
  getRelatedProductsError: AmberError;

  orderTemplatePricing: any;

  tab: ProductDetailsTab;

  createOrderTemplateSuccess: boolean;
}

const defaults: ProductDetailsStateModel = {
  details: undefined,
  relatedProducts: undefined,

  relatedBundles: undefined,
  relatedBundlesTotalCount: undefined,
  relatedBundlesPagination: { page: 1, limit: 10 },

  includedProducts: undefined,
  includedProductsTotalCount: undefined,
  includedProductsPagination: { page: 1, limit: 10 },

  getProductDetailsPending: false,
  getProductDetailsSuccess: true,
  getProductDetailsError: undefined,

  createOrderTemplatePending: false,
  createOrderTemplateError: undefined,
  orderTemplatesList: undefined,

  addOrderTemplateItemPending: false,

  fetchOrderTemplatesMinPending: false,
  fetchOrderTemplatesMinSuccess: false,
  fetchOrderTemplatesMinError: undefined,

  licenseForm: { model: undefined },
  variantForm: { model: undefined },
  selectedVariant: undefined,
  selectedVariantQuantityChangePending: false,
  selectedVariantQuantityChangeSuccess: false,
  selectedVariantQuantityChangeError: undefined,

  languages: [],
  deliveryFormats: [],
  certificateTypes: [],
  productVariantDeliverables: new Map<string, Array<ProductVariantDeliverableModel>>(),
  productVariantDeliverablesPending: false,
  productVariantDeliverablesSuccess: false,

  requestEvaluationCopySuccess: false,
  requestEvaluationCopyPending: false,
  requestEvaluationCopyError: undefined,

  segmentsMapper: new Map<string, Contract>(),
  expiringContract: undefined,

  getRelatedProductsPending: false,
  getRelatedProductsSuccess: false,
  getRelatedProductsError: undefined,

  orderTemplatePricing: {},

  tab: 'about',

  createOrderTemplateSuccess: false,

  addOrderTemplateItemSuccess: false,
  addOrderTemplateItemError: undefined
};

@State<ProductDetailsStateModel>({
  name: 'productDetails',
  defaults
})
export class ProductDetailsState {
  constructor(
    private service: ProductDetailsService,
    private urlStorageService: UrlStorageService,
    private environment: EnvironmentService,
    private utilService: UtilsService,
    private store: Store,
    private orderService: OrderTemplatesService
  ) { }

  @Action(GetProductDetails)
  getProductDetails({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: GetProductDetails) {
    patchState({
      ...getState(),
      getProductDetailsPending: true,
      getProductDetailsSuccess: false,
      getProductDetailsError: undefined,
      details: undefined
    });

    return this.service.getProduct(action.sku)
      .pipe(
        debounceTime(100),
        map((response: any) => response.data && response.data.details),
        tap((details: Product) => {
          const actions: any[] = [new GetProductDetailsSuccess(details)];
          if (this.store.selectSnapshot(AuthTokenState.isLogged) && details.restriction.restricted)
            actions.push(new CheckSegments(details.restriction.accreditationSegments.map(_ => _.code)))
          return dispatch(actions);
        }),
        catchError((graphQLError: GraphQLError) => {
          const error = this.utilService.formatGraphqlError(graphQLError);
          return dispatch(new GetProductDetailsFailure(error));
        })
      )
  }

  @Action(GetProductDetailsSuccess)
  getProductDetailsSuccess(
    { getState, patchState }: StateContext<ProductDetailsStateModel>, action: GetProductDetailsSuccess
  ) {

    let selectedVariant: ProductVariantShort;
    const product = action.payload;
    const variants = product.productVariants;
    let { tab, language, deliveryFormat, certificateType, license } = this.urlStorageService.restore();

    const bundleVariant = variants.find((_: ProductVariantShort) => _.bundleLicensePricingSKU === license);
    if (license && bundleVariant) {
      selectedVariant = bundleVariant;
    }
    else if (this.isParamInvalid(language) ||
      this.isParamInvalid(deliveryFormat) ||
      (product.type === 'Exam' && this.isParamInvalid(certificateType)) ||
        (product.bundle && this.isParamInvalid(license))) {
      const defaultVariant = variants.filter(_ => !!_.defaultVariant)[0];
      selectedVariant = defaultVariant ? defaultVariant : (variants.length ? variants[0] : null);
      language = selectedVariant.language;
      deliveryFormat = selectedVariant.deliveryFormat;
      certificateType = selectedVariant.certificateType;
      license = selectedVariant.bundleLicensePricingSKU
    }
    else {
      selectedVariant = variants.find((_: ProductVariantShort) => {
        return (_.language === language)
            && (_.deliveryFormat.toLowerCase() === deliveryFormat.toLowerCase())
            && (_.certificateType === certificateType)
            && (_.bundleLicensePricingSKU === license);
      });
    }

    let languages = variants.map((_: ProductVariantShort) => _.languageConfig).filter(this.uniqueFromArray);
    let deliveryFormats = variants.map((_: ProductVariantShort) => _.deliveryFormatConfig).filter(this.uniqueFromArray);
    let certificateTypes = variants.map((_: ProductVariantShort) => _.certificateTypeConfig).filter(this.uniqueFromArray);
    patchState({
      getProductDetailsPending: false,
      getProductDetailsSuccess: true,
      getProductDetailsError: undefined,
      details: product,

      variantForm: {
        model: {
          sku: product.sku,
          variantSku: selectedVariant.sku,
          language: language || selectedVariant.language,
          deliveryFormat: deliveryFormat || selectedVariant.deliveryFormat,
          certificateType: certificateType
        }
      },
      licenseForm: {
        model:
          product.bundle && product.bundle.licensePricing && product.bundle.licensePricing.length ?
              (product.bundle.licensePricing.find(license => license.sku === selectedVariant.bundleLicensePricingSKU))
              : null
      },

      selectedVariant: selectedVariant,
      languages: languages,
      deliveryFormats: deliveryFormats,
      certificateTypes: certificateTypes,
      tab: tab || 'about'
    });
  }

  @Action(GetProductDetailsFailure)
  getProductDetailsFailure({ patchState, getState }: StateContext<ProductDetailsStateModel>, action: GetProductDetailsFailure) {
    patchState({
      ...getState(),
      getProductDetailsPending: false,
      getProductDetailsSuccess: false,
      getProductDetailsError: action.payload
    });
  }

  @Action(CheckSegments)
  checkSegments({ patchState, getState, dispatch }: StateContext<ProductDetailsStateModel>, action: CheckSegments) {
    return this.service.checkSegments(action.segments).pipe(
      map((response: AmberResponse<any>) => response.data),
      map((response: any[]) => {
        const segmentsMapper = new Map<string, Contract>();
        response.forEach(_ => {
          segmentsMapper.set(_.key, _.value);
        })

        const expiringContract = this.checkForExpiringContract(segmentsMapper);

        patchState({ segmentsMapper, expiringContract })
        return;
      }),
      catchError((httpError: HttpErrorResponse) => {
        const error = (<AmberResponse<any>>httpError.error).error;
        return dispatch([]);
      })
    );
  }

  private checkForExpiringContract(mapper: Map<string, Contract>) {
    const contracts: Contract[] = mapper && Array.from(mapper.values());
    if (contracts.length) {

      // Get signed contracts, expiring in less than 28 differenceInDays, order by expiry date
      const expiringContracts = contracts
        .filter((_: Contract) => _.status.configKey === 'contract-signed')
        .filter((_: Contract) => _.expiryDate && differenceInDays(new Date(_.expiryDate * 1000), new Date()) <= 28)
        .sort((a, b) => a.expiryDate - b.expiryDate);

      if (expiringContracts.length) return expiringContracts[0];
    }
    return null;
  }

  @Action(GetRelatedProducts)
  getRelatedProducts({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: GetRelatedProducts) {
    patchState({
      getRelatedProductsPending: true,
      getRelatedProductsSuccess: false,
      getRelatedProductsError: undefined,
      relatedProducts: undefined
    });
    return this.service.listRelatedProducts(action.sku, action.limit).pipe(
      map((response: any) => response.data),
      map((response: any) => response.results.products),
      tap(relatedProducts => {
        return dispatch(new GetRelatedProductsSuccess(relatedProducts));
      }),
      catchError((graphQLError: GraphQLError) => {
        const error = this.utilService.formatGraphqlError(graphQLError);
        return dispatch(new GetRelatedProductsError(error));
      })
    );
  }

  @Action(GetRelatedProductsSuccess)
  getRelatedProductsSuccess({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: GetRelatedProductsSuccess) {
    patchState({
      getRelatedProductsPending: false,
      getRelatedProductsSuccess: false,
      getRelatedProductsError: undefined,
      relatedProducts: action.products
    });
  }

  @Action(GetRelatedProductsError)
  getRelatedProductsError({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: GetRelatedProductsError) {
    patchState({
      getRelatedProductsPending: false,
      getRelatedProductsSuccess: false,
      getRelatedProductsError: action.error,
      relatedProducts: undefined
    });
  }

  @Action(ToggleTab)
  toggleTab({ getState, patchState }: StateContext<ProductDetailsStateModel>, { key }: ToggleTab) {
    patchState({
      tab: key
    });
    this.urlStorageService.save(key, getState().variantForm.model, getState().licenseForm.model && getState().licenseForm.model.sku);
  }

  @Action(RestoreState)
  restoreState({ getState, patchState }: StateContext<ProductDetailsStateModel>) {

  }

  @Action(OnChangeVariantForm)
  changeVariantForm({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>) {

    this.urlStorageService.save(getState().tab, getState().variantForm.model, getState().licenseForm.model && getState().licenseForm.model.sku);

    let product = getState().details;
    let variants = product.productVariants;
    let language = getState().variantForm.model.language;
    let deliveryFormat = getState().variantForm.model.deliveryFormat;
    let certificateType = getState().variantForm.model.certificateType;

    const selectedVariant = variants.find((_: ProductVariantShort) => {
      return (_.language === language) && (_.deliveryFormat.toLowerCase() === deliveryFormat.toLowerCase()) && (_.certificateType === certificateType)
    });

    patchState({
      ...getState(),
      variantForm: {
        model: {
          sku: product.sku,
          variantSku: selectedVariant.sku,
          language: language,
          deliveryFormat: deliveryFormat,
          certificateType: certificateType
        }
      }
    });

    dispatch(new OnSelectedVariantChange(selectedVariant));
  }

  @Action(OnChangeBundleVariantForm)
  changeBundleVariantForm({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>) {

    this.urlStorageService.save(getState().tab, getState().variantForm.model, getState().licenseForm.model.sku);

    const product = getState().details;
    const variants = product.productVariants;
    const licenseSku = getState().licenseForm.model.sku;

    const selectedVariant = variants.find((variant: ProductVariantShort) => variant.bundleLicensePricingSKU === licenseSku);

    dispatch(new OnSelectedVariantChange(selectedVariant));
  }

  @Action(SelectProductVariant)
  selectProductVariant({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: SelectProductVariant) {
    const product = getState().details;
    if (!product) {
      return;
    }
    const oldSelectedVariant = getState().selectedVariant;
    const language = action.language || (oldSelectedVariant && oldSelectedVariant.language);
    const deliveryFormat = action.deliveryFormat || (oldSelectedVariant && oldSelectedVariant.deliveryFormat);
    const certificateType = action.certificateType || (oldSelectedVariant && oldSelectedVariant.certificateType);
    const licenseSku = action.license || (oldSelectedVariant && oldSelectedVariant.bundleLicensePricingSKU);

    const newSelectedVariant = product.productVariants.find((_: ProductVariantShort) => {
      return (_.language === language) &&
        (_.deliveryFormat.toLowerCase() === deliveryFormat.toLowerCase()) &&
        (_.certificateType === certificateType) &&
          (_.bundleLicensePricingSKU === licenseSku);
    });
    if (!newSelectedVariant) return;

    patchState({
      selectedVariant: newSelectedVariant,
      variantForm: {
        model: {
          language,
          deliveryFormat,
          certificateType,
          variantSku: newSelectedVariant.sku,
          sku: product.sku
        }
      },
    });
    dispatch(new OnSelectedVariantChange(newSelectedVariant));
  }

  @Action(OnSelectedVariantChange)
  onSelectedVariantChange({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: OnSelectedVariantChange) {
    patchState({
      ...getState(),
      selectedVariant: action.variant
    });
  }

  @Action(SelectedVariantQuantityChange)
  selectedVariantQuantityChange({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: SelectedVariantQuantityChange) {
    patchState({
      selectedVariantQuantityChangePending: true,
      selectedVariantQuantityChangeSuccess: false,
      selectedVariantQuantityChangeError: undefined
    })
    return this.service.computeProductPrice(action.sku, action.quantity).pipe(
      map((response: any) => response.data.results),
      map((data: ProductVariantPricing) => {
        let selectedVariant = { ...getState().selectedVariant };
        selectedVariant.pricing = { ...selectedVariant.pricing, ...data };

        patchState({
          selectedVariantQuantityChangePending: false,
          selectedVariantQuantityChangeSuccess: true,
          selectedVariantQuantityChangeError: undefined,
          selectedVariant: selectedVariant
        })

        return dispatch([])
      }),
      catchError((httpError: HttpErrorResponse) => {

        patchState({
          selectedVariantQuantityChangePending: false,
          selectedVariantQuantityChangeSuccess: false,
          selectedVariantQuantityChangeError: <any>httpError
        })

        return dispatch([]);
      })
    );
  }

  @Action(ComputePriceForOrderTemplate)
  computePriceForOrderTemplate({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: ComputePriceForOrderTemplate) {

    return this.service.computeProductPrice(action.sku, action.quantity).pipe(
      map((response: any) => response.data.results))
      .subscribe((data: ProductVariantPricing) => {
        let pricingPV = {};

        pricingPV[action.sku] = { quantity: action.quantity, pricing: data };

        patchState({
          orderTemplatePricing: { ...getState().orderTemplatePricing, ...pricingPV }
        });
      },
        catchError((httpError: HttpErrorResponse) => {
          return dispatch([]);
        }));
  }

  @Action(RequestEvaluationCopy)
  requestEvaluationCopy({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: RequestEvaluationCopy) {

    patchState({
      requestEvaluationCopySuccess: false,
      requestEvaluationCopyPending: true,
      requestEvaluationCopyError: undefined
    });

    return this.service.requestEvaluationCopy(action.sku, action.email).pipe(
      map((response: AmberResponse<any>) => response.data),
      map((response: any) => {

        return dispatch(new RequestEvaluationCopySuccess(response));
      }),
      catchError((httpError: HttpErrorResponse) => {
        const error = (<AmberResponse<any>>httpError.error).error;
        return dispatch(new RequestEvaluationCopyError(error));
      })
    );
  }

  @Action(RequestEvaluationCopySuccess)
  requestEvaluationCopySuccess({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: RequestEvaluationCopySuccess) {
    patchState({
      requestEvaluationCopySuccess: true,
      requestEvaluationCopyPending: false,
      requestEvaluationCopyError: undefined
    });
  }

  @Action(RequestEvaluationCopyError)
  requestEvaluationCopyError({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: RequestEvaluationCopyError) {
    patchState({
      requestEvaluationCopySuccess: false,
      requestEvaluationCopyPending: false,
      requestEvaluationCopyError: true
    });
  }

  @Action(GetProductVariantDeliverable)
  getProductVariantDeliverable({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: GetProductVariantDeliverable) {

    patchState({
      ...getState(),
      productVariantDeliverablesPending: true,
      productVariantDeliverablesSuccess: false
    });

    const deliverables: Map<string, Array<ProductVariantDeliverableModel>> = getState().productVariantDeliverables;
    if (deliverables.hasOwnProperty(action.sku)) {
      return dispatch(new GetProductVariantDeliverableSuccess(action.sku, deliverables[action.sku]));
    }

    return this.service.getProductVariantDeliverables(action.sku).pipe(
      map((response: AmberResponse<ListProductVariantDeliverableModel>) => response.data[0]),
      map((deliverables: any) => {
        let productContentDeliverableDTOS = deliverables ? deliverables.productContentDeliverableDTOS : null;
        return dispatch(new GetProductVariantDeliverableSuccess(action.sku, productContentDeliverableDTOS));
      }),
      catchError((httpError: HttpErrorResponse) => {
        const error = (<AmberResponse<any>>httpError.error).error;
        return dispatch(new GetProductVariantDeliverableFailure(error));
      })
    );
  }

  @Action(GetProductVariantDeliverableSuccess)
  getProductVariantDeliverableSuccess({ patchState, dispatch, getState }: StateContext<ProductDetailsStateModel>, action: GetProductVariantDeliverableSuccess) {

    let oldDeliverables = getState().productVariantDeliverables;
    let newOne = {};
    newOne[action.sku] = action.deliverables;

    patchState({
      ...getState(),
      productVariantDeliverables: { ...oldDeliverables, ...newOne },
      productVariantDeliverablesPending: false,
      productVariantDeliverablesSuccess: true
    });
  }

  private uniqueFromArray(value, index, self) {
    if (!value || !self) {
      return null;
    }
    return index === self.findIndex((v) => (
      v.configKey === value.configKey
    ));
  }

  private isParamInvalid(param) {
    return !param || param === 'null';
  }

  @Action(ClearProductDetailsState)
  clearProductDetailsState({ patchState, getState }: StateContext<ProductDetailsStateModel>) {
    patchState(defaults);
  }

  @Action(GetAccess)
  getAccess({ dispatch }: StateContext<ProductDetailsStateModel>, action: GetAccess) {
    window.location.href = `${this.environment.saharaUrl}/contracts/buyer/initiate/${action.segment}`;
    return dispatch([]);
  }

  @Action(FetchOrderTemplatesMin)
  fetchOrderTemplatesMin({ getState, patchState, dispatch }: StateContext<ProductDetailsStateModel>, action: FetchOrderTemplatesMin) {
    patchState({
      fetchOrderTemplatesMinPending: true,
      fetchOrderTemplatesMinSuccess: false,
      fetchOrderTemplatesMinError: undefined
    });
    this.service.listMinimal()
      .pipe(map(_ => _.data.listOrderTemplates))
      .subscribe((orderTemplates: any) => {
        return dispatch(new FetchOrderTemplatesMinSuccess(orderTemplates.templates))
      },
        catchError((httpError: HttpErrorResponse) => {
          const error = (<AmberResponse<any>>httpError.error).error;
          return dispatch(new FetchOrderTemplatesMinError(error));
        })
      );
  }

  @Action(FetchOrderTemplatesMinSuccess)
  fetchOrderTemplatesMinSuccess({ getState, patchState, dispatch }: StateContext<ProductDetailsStateModel>, action: FetchOrderTemplatesMinSuccess) {
    patchState({
      orderTemplatesList: action.orderTemplates,
      fetchOrderTemplatesMinPending: false,
      fetchOrderTemplatesMinSuccess: true,
      fetchOrderTemplatesMinError: undefined
    });
  }

  @Action(FetchOrderTemplatesMinError)
  fetchOrderTemplatesMinError({ getState, patchState, dispatch }: StateContext<ProductDetailsStateModel>, action: FetchOrderTemplatesMinError) {
    patchState({
      orderTemplatesList: undefined,
      fetchOrderTemplatesMinPending: false,
      fetchOrderTemplatesMinSuccess: false,
      fetchOrderTemplatesMinError: action.error
    });
  }

  @Action(CreateOrderTemplate)
  createOrderTemplate({ getState, patchState, dispatch }: StateContext<ProductDetailsStateModel>, action: CreateOrderTemplate) {

    patchState({
      createOrderTemplatePending: true,
      createOrderTemplateSuccess: false,
      createOrderTemplateError: undefined
    });
    return this.orderService.createTemplate(action.name)
      .pipe(
        map((response: any) => response.data.name),
        tap((data: any) => {
          patchState({
            createOrderTemplatePending: false,
            createOrderTemplateSuccess: true,
            createOrderTemplateError: undefined
          });

          return dispatch([]);
        }),
        catchError((graphqlError: GraphQLError) => {

          patchState({
            createOrderTemplatePending: false,
            createOrderTemplateSuccess: false,
            createOrderTemplateError: this.utilService.formatGraphqlError(graphqlError)
          });

          return dispatch([]);
        })
      )
  }

  @Action(ClearOrderTemplateState)
  clearOrderTemplateState({ getState, patchState, dispatch }: StateContext<ProductDetailsStateModel>) {
    patchState({
      createOrderTemplatePending: false,
      createOrderTemplateSuccess: false,
      createOrderTemplateError: undefined
    });
  }

  @Action(AddOrderTemplateItem)
  addOrderTemplateItem({ getState, patchState, dispatch }: StateContext<ProductDetailsStateModel>, action: AddOrderTemplateItem) {

    let payload = action.payload;

    patchState({
      addOrderTemplateItemPending: true,
      addOrderTemplateItemSuccess: false,
      addOrderTemplateItemError: undefined
    });
    return this.orderService.addOrderTemplateItem(payload.sku, payload.quantity, payload.orderTemplate)
      .pipe(
        map((response: any) => response.data.name),
        tap((data: any) => {
          patchState({
            addOrderTemplateItemPending: false,
            addOrderTemplateItemSuccess: true,
            addOrderTemplateItemError: undefined
          });

          return dispatch([]);
        }),
        catchError((graphqlError: GraphQLError) => {

          patchState({
            addOrderTemplateItemPending: false,
            addOrderTemplateItemSuccess: false,
            addOrderTemplateItemError: this.utilService.formatGraphqlError(graphqlError)
          });

          return dispatch([]);
        })
      )
  }

  @Action(UpdateProductDetails)
  updateProductDetails({ getState, patchState }: StateContext<ProductDetailsStateModel>, action: UpdateProductDetails) {
    const state = getState();
    patchState({
      details: {
        ...state.details,
        ...action.payload
      }
    });
  }

  @Action(GetProductRelatedBundles)
  getProductRelatedBundles({ patchState }: StateContext<ProductDetailsStateModel>, action: GetProductRelatedBundles) {
    return this.service.getProductRelatedBundles(action.sku, action.pageable)
        .pipe(
            filter((response: AmberResponse<BundleModel[]>) => !!response.data),
            tap((bundles: AmberResponse<BundleModel[]>) => {
              if (bundles) {
                patchState({
                  relatedBundles: bundles.data,
                  relatedBundlesTotalCount: bundles.flags.size
                })
              }
            })
        )
  }

  @Action(GetIncludedProducts)
  getIncludedProducts({ patchState }: StateContext<ProductDetailsStateModel>, action: GetIncludedProducts) {
    return this.service.getIncludedProducts(action.sku, action.pageable)
        .pipe(
            filter((response: any) => !!response.data),
            map((response: any) => response.data.results),
            tap((results: { products: Product[], totalProducts: number }) => {
                patchState({
                  includedProducts: results.products,
                  includedProductsTotalCount: results.totalProducts
                })
            })
        )
  }

  @Action(SetIncludedProductsPagination)
  setIncludedProductsPagination({ patchState }: StateContext<ProductDetailsStateModel>, action: SetIncludedProductsPagination) {
    patchState({
      includedProductsPagination: action.pageable
})
  }

  @Action(SetProductRelatedBundlesPagination)
  setProductRelatedBundlesPagination({ patchState }: StateContext<ProductDetailsStateModel>, action: SetProductRelatedBundlesPagination) {
    patchState({
      relatedBundlesPagination: action.pageable
    })
  }
}
